import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  Clock3,
  FileText,
  MessageCircle,
  Sigma,
  Target,
  Waypoints,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const unit12Topics = [
  "Proof, logic, and algorithms that sharpen mathematical reasoning beyond routine procedures.",
  "Sequences, series, combinatorics, graph theory, and matrices for abstract problem solving.",
  "Trigonometry and complex numbers with the algebraic precision Specialist students need early.",
  "Vectors, functions, and probability foundations developed alongside Mathematical Methods studies.",
];

const unit34Topics = [
  "Advanced functions, algebra, and complex-number-based reasoning across multi-step exam tasks.",
  "Calculus including differential equations, substitution, integration by parts, and volume-style applications.",
  "Vectors and vector-based modelling used for geometric and physical interpretation.",
  "Mechanics, kinematics, and probability/statistics to build true Specialist-level versatility.",
];

const resources = [
  "Access to the Ascend Academy student website with exclusive resources, official exam walkthroughs, practice SAC walkthroughs, useful CAS skills videos, performance guides, worked solutions, and VCAA exam materials.",
  "Practice SACs and problem sets that mirror the depth and abstraction of Specialist assessment.",
  "Practice exams designed to train endurance, speed, and precision under time pressure.",
  "Homework questions that consolidate algebraic fluency and advanced reasoning.",
  "In-depth content explanation, structured notes, and worked solutions for difficult concepts.",
];

const services = [
  "Weekly online content sessions that unpack difficult ideas carefully and systematically.",
  "Remote applied sessions focused on difficult SAC-style and exam-style execution.",
  "Live in-person trial exams near the exam period with in-depth marking, projections, and feedback.",
  "24/7 tutor access so students can ask questions on course material whenever they need support.",
  "Included access to the Ascend Academy website platform and Ascend Academy AI for immediate explanations when students are stuck on Specialist content.",
];

const platformFeatures = [
  "Exclusive Specialist Mathematics resources arranged around advanced topics and exam demands.",
  "Official exam walkthroughs, practice SAC walkthroughs, CAS skills videos, and performance guides for Specialist students.",
  "Worked solutions, practice SACs, and VCAA exam material available inside one platform.",
  "Ascend Academy AI for immediate explanations when students are stuck on Specialist questions.",
];

export const SpecialistMathsPage = () => {
  return (
    <div className="ascend-shell bg-background text-[#10233d]" data-testid="specialist-page">
      <header className="border-b border-[#002147]/10 bg-[#f9f6f0]/90 backdrop-blur-xl" data-testid="specialist-header">
        <div className="ascend-container flex flex-wrap items-center justify-between gap-4 py-4">
          <Link to="/" className="font-display text-2xl font-semibold tracking-tight text-[#002147]" data-testid="specialist-home-link">
            Ascend Academy
          </Link>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="rounded-sm border-[#002147]/15 bg-white hover:bg-[#f4efe5]" data-testid="specialist-back-button">
              <Link to="/">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            <Button asChild className="rounded-sm bg-[#002147] text-white hover:bg-[#001530]" data-testid="specialist-cta-button">
              <a href="/#inquiry">Request an Information Session</a>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="ascend-section border-b border-[#002147]/10" data-testid="specialist-hero">
          <div className="ascend-container grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-6">
              <p className="eyebrow text-[#002147]/65" data-testid="specialist-overline">Subject-specific program</p>
              <h1 className="font-display text-4xl tracking-tight text-[#002147] sm:text-5xl lg:text-6xl" data-testid="specialist-heading">
                VCE Specialist Mathematics
              </h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-700 md:text-base" data-testid="specialist-description">
                The Ascend Specialist Mathematics program is designed for students ready to handle abstraction, proof-style thinking, and high-pressure technical execution.
                It complements Mathematical Methods while pushing students into the depth required for elite Specialist outcomes.
              </p>
            </div>

            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="specialist-summary-card">
              <CardContent className="space-y-5 p-8">
                <p className="eyebrow text-[#f3d8a1]" data-testid="specialist-summary-overline">Program structure</p>
                <div className="grid gap-4">
                  {[
                    { label: "Advanced reasoning", icon: Sigma },
                    { label: "Mechanics + vectors", icon: Waypoints },
                    { label: "Ascend Academy platform", icon: FileText },
                    { label: "24/7 tutor access", icon: MessageCircle },
                  ].map((item) => {
                    const Icon = item.icon;

                    return (
                      <div key={item.label} className="flex items-center gap-3 border border-white/12 bg-white/5 p-4" data-testid={`specialist-summary-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <Icon className="h-4 w-4 text-[#f3d8a1]" />
                        <span className="text-sm text-white/86">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section paper-grid" data-testid="specialist-units-section">
          <div className="ascend-container grid gap-6 lg:grid-cols-2">
            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="specialist-unit12-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="specialist-unit12-heading">Units 1/2 coverage</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="specialist-unit12-list">
                  {unit12Topics.map((topic) => (
                    <li key={topic} className="flex gap-3" data-testid={`specialist-unit12-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="specialist-unit34-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <Target className="h-5 w-5 text-[#f3d8a1]" />
                  <h2 className="font-display text-3xl text-white" data-testid="specialist-unit34-heading">Units 3/4 coverage</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-white/84" data-testid="specialist-unit34-list">
                  {unit34Topics.map((topic) => (
                    <li key={topic} className="flex gap-3" data-testid={`specialist-unit34-topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section bg-[#f4efe5]" data-testid="specialist-resources-services">
          <div className="ascend-container grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="specialist-resources-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="specialist-resources-heading">Resources provided</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="specialist-resources-list">
                  {resources.map((resource) => (
                    <li key={resource} className="flex gap-3" data-testid={`specialist-resource-${resource.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{resource}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="section-frame rounded-sm bg-white shadow-none" data-testid="specialist-services-card">
              <CardContent className="space-y-6 p-8">
                <div className="flex items-center gap-3">
                  <Clock3 className="h-5 w-5 text-[#002147]" />
                  <h2 className="font-display text-3xl text-[#002147]" data-testid="specialist-services-heading">Services provided</h2>
                </div>
                <ul className="grid gap-3 text-sm leading-7 text-slate-700" data-testid="specialist-services-list">
                  {services.map((service) => (
                    <li key={service} className="flex gap-3" data-testid={`specialist-service-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#c5a059]" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section" data-testid="specialist-platform-section">
          <div className="ascend-container">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="specialist-platform-card">
              <CardContent className="grid gap-6 p-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                <div className="space-y-4">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="specialist-platform-overline">Ascend Academy Platform</p>
                  <h2 className="font-display text-3xl text-white md:text-4xl" data-testid="specialist-platform-heading">
                    A dedicated Specialist support website for deeper revision and faster help.
                  </h2>
                  <p className="text-sm leading-7 text-white/82" data-testid="specialist-platform-description">
                    The platform gives Specialist students a single place to access weekly homework, practice SACs, practice exams, official walkthrough videos, performance guides, worked solutions, and immediate explanations whenever a difficult question appears.
                  </p>
                  <Button asChild className="rounded-sm bg-[#c5a059] text-[#002147] hover:bg-[#b48d4d]" data-testid="specialist-platform-cta">
                    <a href="/#inquiry">
                      Ask About the Platform
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                </div>
                <div className="grid gap-4" data-testid="specialist-platform-list">
                  {platformFeatures.map((feature) => (
                    <div key={feature} className="flex gap-3 border border-white/12 bg-white/5 p-4" data-testid={`specialist-platform-feature-${feature.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#f3d8a1]" />
                      <p className="text-sm leading-7 text-white/84">{feature}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="ascend-section" data-testid="specialist-cta-section">
          <div className="ascend-container">
            <Card className="section-frame rounded-sm bg-[#002147] text-white shadow-none" data-testid="specialist-cta-card">
              <CardContent className="flex flex-col gap-6 p-8 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-3">
                  <p className="eyebrow text-[#f3d8a1]" data-testid="specialist-cta-overline">Next step</p>
                  <h2 className="font-display text-3xl text-white" data-testid="specialist-cta-heading">Explore whether the Ascend Specialist pathway matches the student’s ambition.</h2>
                  <p className="max-w-3xl text-sm leading-7 text-white/82" data-testid="specialist-cta-copy">
                    Enquire for a free information session to discuss expectations, the support structure, and how Ascend prepares Specialist students for demanding SACs and exams.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button asChild className="rounded-sm bg-[#c5a059] text-[#002147] hover:bg-[#b48d4d]" data-testid="specialist-request-button">
                    <a href="/#inquiry">
                      Request Information Session
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="rounded-sm border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white" data-testid="specialist-methods-link">
                    <Link to="/math-methods">View Mathematical Methods</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};